import { DetectedExternalWallData } from '@core/models/project.models';

const M_TO_FT = 3.28084;

interface Point {
  x: number;
  y: number;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizePoints(points: unknown): [number, number][] {
  if (!Array.isArray(points)) return [];
  return points
    .map((point): [number, number] | null => {
      if (!Array.isArray(point) || point.length < 2) return null;
      const x = toFiniteNumber(point[0]);
      const y = toFiniteNumber(point[1]);
      return x === null || y === null ? null : [x, y];
    })
    .filter((point): point is [number, number] => point !== null);
}

function polylineLength(points: [number, number][]): number {
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i - 1];
    const [x2, y2] = points[i];
    length += Math.hypot(x2 - x1, y2 - y1);
  }
  return length;
}

function centroid(points: [number, number][]): Point | null {
  if (!points.length) return null;
  const sums = points.reduce(
    (acc, [x, y]) => ({ x: acc.x + x, y: acc.y + y }),
    { x: 0, y: 0 },
  );
  return { x: sums.x / points.length, y: sums.y / points.length };
}

function distancePointToLine(point: Point, start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = Math.hypot(dx, dy);
  if (denominator <= 0) return 0;
  return Math.abs((dy * point.x) - (dx * point.y) + (end.x * start.y) - (end.y * start.x)) / denominator;
}

function estimateFeetPerPointFromWalls(walls: DetectedExternalWallData[]): number | null {
  const ratios = walls
    .map((wall) => {
      const pointLength = polylineLength(wall.points);
      return pointLength > 0 && wall.lengthFt > 0 ? wall.lengthFt / pointLength : null;
    })
    .filter((ratio): ratio is number => ratio !== null);
  if (!ratios.length) return null;
  return ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;
}

export function calculateExternalWallMetrics(contours: unknown[]): {
  walls: DetectedExternalWallData[];
  perimeterFt: number;
  longestFt: number;
  perpendicularFt: number;
} {
  const walls = contours
    .map((contour): DetectedExternalWallData | null => {
      const data = contour as Record<string, unknown>;
      const lengthFt = toFiniteNumber(data['length_ft'])
        ?? toFiniteNumber(data['lengthFt'])
        ?? ((toFiniteNumber(data['length']) ?? 0) * M_TO_FT);
      const points = normalizePoints(data['points']);
      return lengthFt > 0 || points.length
        ? { lengthFt: Math.max(lengthFt, 0), points }
        : null;
    })
    .filter((wall): wall is DetectedExternalWallData => wall !== null);

  const perimeterFt = walls.reduce((sum, wall) => sum + wall.lengthFt, 0);
  const longestFt = walls.reduce((max, wall) => Math.max(max, wall.lengthFt), 0);
  const perpendicularFt = calculatePerpendicularExternalWallFt(walls);

  return { walls, perimeterFt, longestFt, perpendicularFt };
}

export function calculatePerpendicularExternalWallFt(walls: DetectedExternalWallData[]): number {
  const wallsWithPoints = walls.filter((wall) =>
    wall.points.length >= 2 && wall.lengthFt > 0,
  );
  if (wallsWithPoints.length >= 2) {
    const longestWall = wallsWithPoints.reduce((best, wall) =>
      wall.lengthFt > best.lengthFt ? wall : best,
    );
    const [startRaw, endRaw] = [longestWall.points[0], longestWall.points[longestWall.points.length - 1]];
    const start = { x: startRaw[0], y: startRaw[1] };
    const end = { x: endRaw[0], y: endRaw[1] };
    const feetPerPoint = estimateFeetPerPointFromWalls(wallsWithPoints);
    if (feetPerPoint !== null) {
      const farthestPointDistance = wallsWithPoints
        .filter((wall) => wall !== longestWall)
        .reduce((max, wall) => {
          const center = centroid(wall.points);
          return center ? Math.max(max, distancePointToLine(center, start, end)) : max;
        }, 0);
      if (farthestPointDistance > 0) return farthestPointDistance * feetPerPoint;
    }
  }

  const sortedLengths = walls
    .map((wall) => wall.lengthFt)
    .filter((length) => length > 0)
    .sort((a, b) => b - a);
  return sortedLengths[1] ?? sortedLengths[0] ?? 0;
}
